import Router from "next/router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IntlShape } from "react-intl";
import { useDispatch } from "react-redux";

import {
  startUniversalLoading,
  stopUniversalLoading,
} from "shared/data/actions/loader";
import { BaggageLimit } from "shared/gol-api/types/flightSearch";
import { getBaggageConditions } from "shared/lib/golCopy";
import {
  BaggageDetail,
  SegmentBaggage,
} from "shared/lib/golObjectTypes/BaggageDetails/BaggageDetails";
import { ResponseObject } from "shared/lib/golObjectTypes/ResponseObject";
import Logger from "shared/services/Logger";

import Img from "@components/FileServer/Img";
import { GetRefsForFlightOffer } from "@components/Flights/FlightAdditionalInfo";
import FormattedMessage from "@components/UI/FormattedMessage";
import Modal from "@components/UI/Modal";
import Switch from "@components/UI/Switch";

import Footer from "./Footer";
import BaggageRow from "./Row";

type Props = {
  intl: IntlShape;
  onClose: () => void;
  getRefsForFlightOffer: GetRefsForFlightOffer;
  baggageInfo: BaggageLimit;
};

export type Column = {
  label: string;
  width: number;
};

const AdditionalBaggageModal = ({
  intl,
  onClose,
  getRefsForFlightOffer,
  baggageInfo,
}: Props) => {
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState<BaggageDetail[]>([]);
  const [unit, setUnit] = useState<"KILOGRAMS" | "POUNDS">("KILOGRAMS");

  /* eslint-disable react-hooks/exhaustive-deps */
  const onMountFetch = useCallback(async () => {
    dispatch(startUniversalLoading());
    const refs = getRefsForFlightOffer();

    if (
      !baggageInfo.DetailsAvailable &&
      baggageInfo.BaggageSelectionAvailable
    ) {
      dispatch(stopUniversalLoading());
      setLoading(false);
      return;
    }

    const baggageResponse: ResponseObject & {
      cause?: Error;
    } = await getBaggageConditions(refs, Router.router.query.currency);

    if (!baggageResponse.success) {
      dispatch(stopUniversalLoading());
      setLoading(false);
      Logger.error("Baggage details couldn't be loaded.", {
        cause: baggageResponse.cause,
        tags: { area: "baggage" },
        extra: {
          errorCode: baggageResponse.errorCode,
          errorMsg: baggageResponse.errorMsg,
          detailsAvailable: baggageInfo.DetailsAvailable,
          baggageSelectionAvailable: baggageInfo.BaggageSelectionAvailable,
        },
      });
      return;
    }

    setData([...baggageResponse.data.BaggageDetails.BaggageDetail]);
    dispatch(stopUniversalLoading());
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    onMountFetch();
  }, [onMountFetch]);

  const columns: Column[] = useMemo(
    () => [
      {
        label: `${intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.from",
        })} - ${intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.to",
        })}`,
        width: 240,
      },
      {
        label: intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.checkedIn",
        }),
        width: 300,
      },
      {
        label: intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.carryOn",
        }),
        width: 300,
      },
      {
        label: "",
        width: 40,
      },
    ],
    [intl]
  );

  const columnIcons: Record<number, string> = {
    // The legacy filenames describe the opposite artwork.
    1: "/static/images/ico-carry-on-baggage.svg",
    2: "/static/images/ico-checked-in-baggage.svg",
  };

  const columnIconAltText: Record<number, string> = {
    1: intl.formatMessage({ id: "FlightAdditionalInfoBaggage.checkedIn" }),
    2: intl.formatMessage({ id: "FlightAdditionalInfoBaggage.carryOn" }),
  };

  const baggageItems = useMemo(() => {
    const body = {
      passenger: [],
      infant: [],
    };

    let infantDisplayed = false;
    let passengerDisplayed = false;

    /* eslint-disable */
    for (const segment of data) {
      segment.SegmentBaggage.forEach(
        (segmentBaggage: SegmentBaggage, index: number) => {
          if (segmentBaggage.BaggageItem === undefined) {
            return;
          }

          if (!passengerDisplayed && segment.PassengerType !== "INF") {
            if (index === segment.SegmentBaggage.length - 1) {
              passengerDisplayed = true;
            }

            body.passenger.push(
              <BaggageRow
                intl={intl}
                key={index}
                data={segmentBaggage}
                unit={unit}
                columns={columns}
              />
            );
          } else if (segment.PassengerType === "INF" && !infantDisplayed) {
            if (index === segment.SegmentBaggage.length - 1) {
              infantDisplayed = true;
            }
            body.infant.push(
              <BaggageRow
                intl={intl}
                key={index}
                data={segmentBaggage}
                unit={unit}
                columns={columns}
              />
            );
          }
        }
      );

      if (infantDisplayed && passengerDisplayed) {
        break;
      }
    }

    return body;
  }, [data, unit, columns, intl]);

  if (isLoading) {
    return null;
  }

  const isEmpty = !data.length;

  const detailInNextStep =
    baggageInfo.DetailsAvailable === "false" &&
    baggageInfo.BaggageSelectionAvailable === "true";

  const onSwitchChange = (item: string) => {
    setUnit(item === "kg" ? "KILOGRAMS" : "POUNDS");
  };

  return (
    <Modal isActive onClose={onClose}>
      <div
        className="flight-additional-info-baggage-modal-content"
        style={
          isEmpty
            ? {
                textAlign: "center",
              }
            : {}
        }
      >
        {isEmpty ? (
          detailInNextStep ? (
            <FormattedMessage id="FlightAdditionalInfoBaggage.baggageNextStepInfo" />
          ) : (
            <FormattedMessage id="FlightAdditionalInfoBaggage.noDataBaggage" />
          )
        ) : (
          <>
            <div className="flight-additional-info-baggage-modal-header-wrap">
              <h3 className="flight-additional-info-baggage-modal-header">
                <FormattedMessage id="FlightAdditionalInfoBaggage.title" />
              </h3>
              <Switch items={["kg", "lb"]} onChange={onSwitchChange} />
            </div>
            <div className="flight-additional-info-baggage-modal-table">
              <div className="flight-additional-info-baggage-modal-table-header">
                {columns.map((column, index) => (
                  <div
                    key={index}
                    className="flight-additional-info-baggage-modal-table-header-column"
                    style={{ width: column.width }}
                  >
                    {columnIcons[index] && (
                      <Img
                        src={columnIcons[index]}
                        alt={columnIconAltText[index] || "Baggage icon"}
                        className="flight-additional-info-baggage-modal-header-icon-header"
                      />
                    )}
                    {column.label}
                  </div>
                ))}
              </div>
              {baggageItems.passenger}
              {!!baggageItems.infant.length && (
                <>
                  <div className="flight-additional-info-baggage-modal-infantheader">
                    <FormattedMessage id="FlightAdditionalInfoBaggage.infantException" />
                  </div>
                  {baggageItems.infant}
                </>
              )}
            </div>
            <Footer />
          </>
        )}
      </div>
    </Modal>
  );
};

export default AdditionalBaggageModal;
